/**
 * Grants ADDRESS_READER to an existing member, as its own deliberate act
 * (§5.1): the role is never offered by the invitation form, so this
 * dialog -- not the ordinary role picker -- is the one path that reaches it
 * (`MemberList` ignores an ADDRESS_READER selection from its own picker; see
 * that module's doc comment). Its trigger lives in `MemberList`'s row menu;
 * this component only renders the confirmation itself, controlled by `open`.
 *
 * §4.7/§16: a second factor here is an accepted, still-open risk. This
 * dialog is the mitigation shipped instead -- a grant needs a deliberate
 * second click naming exactly what the role does and nothing more, not one
 * click that could be an accident.
 */
import type { ReactNode } from 'react';

import { ConfirmDialog } from '../../../chrome/form';
import type { MembershipRow } from '../api/schemas';
import { tt } from '../i18n';
import { tm } from '../pages/team.messages';
import { useChangeRole } from './useMembers';

export function GrantAddressReader(props: {
  tenantId: string;
  membership: MembershipRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { tenantId, membership, open, onOpenChange } = props;
  const changeRole = useChangeRole(tenantId);

  // Already holds it -- nothing left for this control to do. Not a case
  // `RolePicker` needs to know about; it renders choices, this decides
  // nothing about who may see it.
  if (membership.role === 'ADDRESS_READER') return null;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={tm('member.grantAddress')}
      description={tt('members.grant_address.confirm', {
        email: membership.email ?? tt('members.noEmail'),
      })}
      confirmLabel={tm('grant.confirm')}
      pending={changeRole.isPending}
      failure={changeRole.error?.failure ?? null}
      onConfirm={() =>
        changeRole.mutateAsync({ membershipId: membership.id, role: 'ADDRESS_READER' })
      }
    />
  );
}
